import React, { useState } from "react";
import { useParams } from "react-router-dom"; // Importar useParams
import safeLogger from "../utils/safeLogger";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Paper,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const ResetPassword = () => {
  const { token, email } = useParams(); // Captura o token e o e-mail da URL
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState(""); // Estado para o erro de coincidência de senhas
  const [successMessage, setSuccessMessage] = useState("");

  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[@$!%*?&]/.test(password),
    };
    setPasswordRequirements(requirements);
    return Object.values(requirements).every(Boolean);
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    validatePassword(password);
  };

  const handleConfirmPasswordChange = (e) => {
    const confirmPass = e.target.value;
    setConfirmPassword(confirmPass);

    // Verifica a coincidência das senhas em tempo real
    if (newPassword !== confirmPass) {
      setPasswordMatchError("As senhas não coincidem.");
    } else {
      setPasswordMatchError(""); // Limpa o erro se as senhas coincidirem
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");

    if (passwordMatchError) {
      return; // Não permite enviar o formulário se houver erro
    }

    try {
      const response = await fetch("https://codefort.onrender.com/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, email, newPassword }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      setSuccessMessage("Senha redefinida com sucesso! Redirecionando para login...");
      setTimeout(() => {
        window.location.href = "/login";
      }, 3000);
    } catch (error) {
      safeLogger.error("Erro ao redefinir senha:", error.message);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Redefinir Senha
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Nova Senha"
            type={showPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={handlePasswordChange}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mb: 2, textAlign: "left" }}>
            <Typography variant="body2">
              <span
                style={{
                  color: passwordRequirements.minLength ? "green" : "red",
                }}
              >
                {passwordRequirements.minLength ? "✔" : "✘"} Mínimo de 6 caracteres
              </span>
            </Typography>
            <Typography variant="body2">
              <span
                style={{
                  color: passwordRequirements.uppercase ? "green" : "red",
                }}
              >
                {passwordRequirements.uppercase ? "✔" : "✘"} Pelo menos 1 letra maiúscula
              </span>
            </Typography>
            <Typography variant="body2">
              <span
                style={{
                  color: passwordRequirements.lowercase ? "green" : "red",
                }}
              >
                {passwordRequirements.lowercase ? "✔" : "✘"} Pelo menos 1 letra minúscula
              </span>
            </Typography>
            <Typography variant="body2">
              <span
                style={{
                  color: passwordRequirements.number ? "green" : "red",
                }}
              >
                {passwordRequirements.number ? "✔" : "✘"} Pelo menos 1 número
              </span>
            </Typography>
            <Typography variant="body2">
              <span
                style={{
                  color: passwordRequirements.specialChar ? "green" : "red",
                }}
              >
                {passwordRequirements.specialChar ? "✔" : "✘"} Pelo menos 1 caractere especial
              </span>
            </Typography>
          </Box>
          <TextField
            label="Confirmação de Senha"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange} // Validação em tempo real
            error={!!passwordMatchError} // Exibe o erro visualmente
            helperText={
              passwordMatchError && (
                <span style={{ color: "red", fontWeight: "bold" }}>
                  {passwordMatchError}
                </span>
              )
            }
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={toggleConfirmPasswordVisibility}>
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
          >
            Redefinir Senha
          </Button>
          {successMessage && (
            <Typography sx={{ color: "green", fontWeight: "bold", mt: 2 }}>
              {successMessage}
            </Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPassword;
